import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import appConfig from '../../config/appConfig';
import { api, apiRoutes } from '../../api';
import Header from '../../shared/layout/Header';
import developersApi from '../../api/services/developerService';
import StepIndicator from '../AgentApplication/components/StepIndicator';
import { ProjectLocationMap } from './projectWizard/WizardControls';
import './DevelopersPanel.css';

const developerStepLabels = ['اطلاعات شرکت', 'مدارک و تایید', 'سوابق کاری', 'مرجع مشتری', 'بررسی'];
const developerWithAccountStepLabels = ['حساب کاربری', ...developerStepLabels];

const emptyAccount = {
  full_name: '',
  national_id: '',
  phone: '',
  email: ''
};

const emptyFirm = {
  legal_name: '',
  display_name: '',
  registration_number: '',
  phone: '',
  website: '',
  description: ''
};

const emptyPortfolio = {
  name: '',
  address: '',
  description: '',
  stage: 'COMPLETED',
  construction_started_at: '',
  completed_at: '',
  total_units: '',
  latitude: '',
  longitude: ''
};

const emptyReference = {
  project: '',
  name: '',
  email: '',
  phone_number: '',
  address: '',
  unit_reference: ''
};

function normalizeError(error) {
  const data = error?.response?.data;
  if (!data) return 'خطا در ارتباط با سرور';
  if (typeof data === 'string') return data;
  if (data.detail) return data.detail;
  return JSON.stringify(data);
}

function markDeveloperProfile(firm) {
  const profile = JSON.parse(localStorage.getItem('user_profile') || '{}');
  localStorage.setItem('user_profile', JSON.stringify({
    ...profile,
    is_developer: true,
    developer_id: firm.id,
    developer_name: firm.display_name || firm.legal_name
  }));
  window.dispatchEvent(new Event('storage'));
}

function Field({ label, children }) {
  return <label className="dev-field"><span>{label}</span>{children}</label>;
}

function getLocationFields(location) {
  const coordinates = location?.coordinates;
  if (!Array.isArray(coordinates) || coordinates.length !== 2) {
    return { latitude: '', longitude: '' };
  }
  return {
    latitude: coordinates[1] != null ? String(coordinates[1]) : '',
    longitude: coordinates[0] != null ? String(coordinates[0]) : ''
  };
}

function buildLocationPayload(latitude, longitude) {
  const lat = Number(latitude);
  const lng = Number(longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }
  return { type: 'Point', coordinates: [lng, lat] };
}

function storeDeveloperAuth(authResult) {
  localStorage.setItem(appConfig.auth.accessTokenKey, authResult.access);
  localStorage.setItem(appConfig.auth.refreshTokenKey, authResult.refresh);
  localStorage.setItem('user_id', String(authResult.user_id));
  localStorage.setItem('username', authResult.username);
  localStorage.setItem('user_profile', JSON.stringify({
    first_name: authResult.first_name || '',
    last_name: authResult.last_name || '',
    phone: authResult.phone || '',
    email: authResult.email || '',
    national_id: authResult.national_id || '',
    is_agent: false,
    is_developer: authResult.is_developer || false,
  }));
}

function LoginPrompt({ nationalId, onLoginSuccess, onBack }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const tryLogin = async (username) => {
    const res = await api.post(apiRoutes.auth.token, { username, password });
    const profile = await api.get(apiRoutes.auth.user, {
      headers: { Authorization: `Bearer ${res.data.access}` }
    });
    storeDeveloperAuth({
      access: res.data.access,
      refresh: res.data.refresh,
      user_id: profile.data.id,
      username: profile.data.username,
      first_name: profile.data.first_name,
      last_name: profile.data.last_name,
      phone: profile.data.phone,
      email: profile.data.email,
      national_id: profile.data.national_id,
      is_developer: false
    });
  };

  const handleLogin = async () => {
    if (!password.trim()) {
      setError('رمز عبور را وارد کنید');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await tryLogin(nationalId);
      onLoginSuccess();
    } catch {
      try {
        await tryLogin(`dev_${nationalId}`);
        onLoginSuccess();
      } catch {
        setError('نام کاربری یا رمز عبور اشتباه است');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dev-panel wide full">
      <div className="dev-alert error">
        این کد ملی قبلاً در سیستم ثبت شده است. برای ادامه با حساب کاربری خود وارد شوید.
      </div>
      <div className="dev-form-stack">
        <Field label="کد ملی">
          <input className="dev-input" value={nationalId} disabled />
        </Field>
        <Field label="رمز عبور">
          <input
            className="dev-input"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            onKeyDown={(event) => event.key === 'Enter' && handleLogin()}
            autoFocus
            required
          />
        </Field>
        {error && <div className="dev-alert error">{error}</div>}
      </div>
      <div className="dev-wizard-nav">
        <button type="button" className="dev-btn secondary" onClick={onBack} disabled={loading}>مرحله قبل</button>
        <button type="button" className="dev-btn primary" onClick={handleLogin} disabled={loading}>
          {loading ? 'در حال ورود...' : 'ورود و ادامه'}
        </button>
      </div>
    </div>
  );
}

function DeveloperRegistration() {
  const [hasAccountStep] = useState(() => !localStorage.getItem(appConfig.auth.accessTokenKey));
  const [step, setStep] = useState(1);
  const [accountForm, setAccountForm] = useState(emptyAccount);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [firm, setFirm] = useState(null);
  const [firmForm, setFirmForm] = useState(emptyFirm);
  const [portfolioForm, setPortfolioForm] = useState(emptyPortfolio);
  const [referenceForm, setReferenceForm] = useState(emptyReference);
  const [portfolioProjects, setPortfolioProjects] = useState([]);
  const [cases, setCases] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    if (localStorage.getItem(appConfig.auth.accessTokenKey)) {
      loadMine();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = async (task, message) => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const result = await task();
      if (message) setNotice(message);
      return result;
    } catch (err) {
      setError(normalizeError(err));
      return null;
    } finally {
      setBusy(false);
    }
  };

  const loadMine = async () => {
    await run(async () => {
      const firms = await developersApi.listFirms();
      const current = firms[0] || null;
      setFirm(current);
      if (current) {
        markDeveloperProfile(current);
        setFirmForm({
          legal_name: current.legal_name || '',
          display_name: current.display_name || '',
          registration_number: current.registration_number || '',
          phone: current.phone || '',
          website: current.website || '',
          description: current.description || ''
        });
        const [projects, verificationCases] = await Promise.all([
          developersApi.listPortfolioProjects(current.id),
          developersApi.listVerificationCases(current.id)
        ]);
        setPortfolioProjects(projects);
        setCases(verificationCases);
      }
    });
  };

  const saveAccount = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const authResult = await developersApi.developerApplicantAuth({
        national_id: accountForm.national_id,
        full_name: accountForm.full_name,
        phone: accountForm.phone,
        email: accountForm.email || '',
      });
      storeDeveloperAuth(authResult);
      setFirmForm((current) => ({
        ...current,
        phone: current.phone || authResult.phone || accountForm.phone,
      }));
      await loadMine();
      next();
    } catch (err) {
      if (err?.response?.status === 409) {
        setShowLoginPrompt(true);
      } else {
        setError(normalizeError(err));
      }
    } finally {
      setBusy(false);
    }
  };

  const saveFirm = async (event) => {
    event.preventDefault();
    const saved = await run(
      () => firm ? developersApi.updateFirm(firm.id, firmForm) : developersApi.createFirm(firmForm),
      'پروفایل سازنده ذخیره شد.'
    );
    if (saved) {
      setFirm(saved);
      markDeveloperProfile(saved);
      await loadMine();
    }
    return saved;
  };

  const uploadDocument = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !firm) return;
    const fd = new FormData();
    fd.append('type', 'CERTIFICATE');
    fd.append('file', file);
    await run(() => developersApi.uploadFirmDocument(firm.id, fd), 'مدرک بارگذاری شد.');
    await loadMine();
  };

  const requestBasic = () => run(
    () => developersApi.requestBasicVerification(firm.id),
    'درخواست تایید پایه ارسال شد.'
  ).then(loadMine);

  const requestMedium = () => run(
    () => developersApi.requestMediumVerification(firm.id),
    'درخواست تایید متوسط ارسال شد.'
  ).then(loadMine);

  const savePortfolio = async (event) => {
    event.preventDefault();
    if (!firm) return;
    const { latitude, longitude, ...projectFields } = portfolioForm;
    const saved = await run(() => developersApi.createPortfolioProject({
      ...projectFields,
      developer: firm.id,
      construction_started_at: portfolioForm.construction_started_at || null,
      completed_at: portfolioForm.completed_at || null,
      total_units: portfolioForm.total_units || null,
      location: buildLocationPayload(latitude, longitude)
    }), 'پروژه سابقه کاری ثبت شد.');
    setPortfolioForm(emptyPortfolio);
    await loadMine();
    return saved;
  };

  const addReference = async (event) => {
    event.preventDefault();
    await run(() => developersApi.addCustomerReference(referenceForm.project, referenceForm), 'مرجع مشتری ثبت شد.');
    setReferenceForm(emptyReference);
    await loadMine();
  };

  const labels = hasAccountStep ? developerWithAccountStepLabels : developerStepLabels;
  const totalSteps = labels.length;
  const accountStep = hasAccountStep ? 1 : 0;
  const firmStep = hasAccountStep ? 2 : 1;
  const documentsStep = hasAccountStep ? 3 : 2;
  const portfolioStep = hasAccountStep ? 4 : 3;
  const referencesStep = hasAccountStep ? 5 : 4;
  const reviewStep = hasAccountStep ? 6 : 5;
  const next = () => setStep((current) => Math.min(current + 1, totalSteps));
  const back = () => setStep((current) => Math.max(current - 1, 1));

  const completeLogin = async () => {
    setShowLoginPrompt(false);
    await loadMine();
    next();
  };

  return (
    <div className="dev-page" dir="rtl">
      <Header />
      <main className="dev-shell">
        <section className="dev-hero compact">
          <div>
            <p className="dev-kicker">ثبت نام سازندگان</p>
            <h1>ساخت پروفایل سازنده و درخواست تایید</h1>
            <p>ابتدا اطلاعات حقوقی شرکت را کامل کنید، مدارک را بارگذاری کنید و سپس در صورت نیاز پروژه های سابق و مراجع مشتری را اضافه کنید.</p>
          </div>
          {firm && <Link className="dev-btn primary" to="/developer-projects">ثبت پروژه</Link>}
        </section>

        {error && <div className="dev-alert error">{error}</div>}
        {notice && <div className="dev-alert success">{notice}</div>}

        <StepIndicator currentStep={step} totalSteps={totalSteps} labels={labels} />

        <section className="dev-grid dev-wizard-body">
          {hasAccountStep && step === accountStep && (showLoginPrompt ? (
            <LoginPrompt
              nationalId={accountForm.national_id}
              onLoginSuccess={completeLogin}
              onBack={() => setShowLoginPrompt(false)}
            />
          ) : (
            <form className="dev-panel wide full" onSubmit={saveAccount}>
              <div className="dev-panel-head">
                <div>
                  <h2>اطلاعات حساب کاربری</h2>
                  <p>برای ثبت سازنده، ابتدا یک حساب کاربری بدون نیاز به ورود ساخته می شود.</p>
                </div>
              </div>
              <div className="dev-form-grid">
                <Field label="نام و نام خانوادگی"><input className="dev-input" value={accountForm.full_name} onChange={(e) => setAccountForm({ ...accountForm, full_name: e.target.value })} /></Field>
                <Field label="کد ملی"><input className="dev-input" value={accountForm.national_id} onChange={(e) => setAccountForm({ ...accountForm, national_id: e.target.value })} pattern="[0-9]{10}" title="کد ملی باید ۱۰ رقم باشد" required /></Field>
                <Field label="شماره تلفن"><input className="dev-input" value={accountForm.phone} onChange={(e) => setAccountForm({ ...accountForm, phone: e.target.value })} required /></Field>
                <Field label="ایمیل"><input className="dev-input" type="email" value={accountForm.email} onChange={(e) => setAccountForm({ ...accountForm, email: e.target.value })} /></Field>
              </div>
              <div className="dev-wizard-nav"><span /><button type="submit" className="dev-btn primary" disabled={busy}>ادامه</button></div>
            </form>
          ))}

          {step === firmStep && <form className="dev-panel wide full" onSubmit={async (event) => { const saved = await saveFirm(event); if (saved) next(); }}>
            <div className="dev-panel-head">
              <div>
                <h2>اطلاعات سازنده</h2>
                <p>بعد از ذخیره، حساب شما به عنوان سازنده شناخته می شود.</p>
              </div>
            </div>
            <div className="dev-form-grid">
              <Field label="نام حقوقی"><input className="dev-input" value={firmForm.legal_name} onChange={(e) => setFirmForm({ ...firmForm, legal_name: e.target.value })} required /></Field>
              <Field label="نام نمایشی"><input className="dev-input" value={firmForm.display_name} onChange={(e) => setFirmForm({ ...firmForm, display_name: e.target.value })} required /></Field>
              <Field label="شماره ثبت"><input className="dev-input" value={firmForm.registration_number} onChange={(e) => setFirmForm({ ...firmForm, registration_number: e.target.value })} /></Field>
              <Field label="تلفن کاری"><input className="dev-input" value={firmForm.phone} onChange={(e) => setFirmForm({ ...firmForm, phone: e.target.value })} /></Field>
              <Field label="وب سایت"><input className="dev-input" value={firmForm.website} onChange={(e) => setFirmForm({ ...firmForm, website: e.target.value })} /></Field>
              <Field label="توضیحات"><textarea className="dev-textarea" value={firmForm.description} onChange={(e) => setFirmForm({ ...firmForm, description: e.target.value })} /></Field>
            </div>
            <div className="dev-wizard-nav"><span /><button type="submit" className="dev-btn primary" disabled={busy}>ذخیره و ادامه</button></div>
          </form>}

          {step === documentsStep && <div className="dev-panel wide">
            <h2>مدارک و تایید</h2>
            <input className="dev-input" type="file" disabled={!firm || busy} onChange={uploadDocument} />
            <div className="dev-actions">
              <button className="dev-btn secondary" disabled={!firm || busy} onClick={requestBasic}>درخواست تایید پایه</button>
              <button className="dev-btn secondary" disabled={!firm || busy} onClick={requestMedium}>درخواست تایید متوسط</button>
            </div>
            <div className="dev-docs">
              {(firm?.documents || []).map((doc) => <a key={doc.id} href={doc.file_url} target="_blank" rel="noreferrer">{doc.type}</a>)}
            </div>
            <div className="dev-wizard-nav"><button type="button" className="dev-btn secondary" onClick={back}>مرحله قبل</button><button type="button" className="dev-btn primary" onClick={next} disabled={!firm}>ذخیره و ادامه</button></div>
          </div>}

          {step === documentsStep && <div className="dev-panel">
            <h2>پرونده های بررسی</h2>
            <div className="dev-list">
              {cases.map((item) => (
                <div className="dev-list-row static" key={item.id}>
                  <span>پرونده {item.number}</span>
                  <small>{item.level} - {item.status}</small>
                </div>
              ))}
            </div>
          </div>}

          {step === portfolioStep && <form className="dev-panel wide" onSubmit={savePortfolio}>
            <div className="dev-panel-head">
              <div>
                <h2>پروژه های سابق</h2>
                <p>ابتدا پروژه های قبلی سازنده را ثبت کنید. در مرحله بعد اطلاعات مشتریان مرتبط با همین پروژه ها دریافت می شود.</p>
              </div>
            </div>
            <div className="dev-form-grid">
              <Field label="نام پروژه"><input className="dev-input" value={portfolioForm.name} onChange={(e) => setPortfolioForm({ ...portfolioForm, name: e.target.value })} /></Field>
              <Field label="آدرس"><input className="dev-input" value={portfolioForm.address} onChange={(e) => setPortfolioForm({ ...portfolioForm, address: e.target.value })} /></Field>
              <Field label="شروع ساخت"><input className="dev-input" type="date" value={portfolioForm.construction_started_at} onChange={(e) => setPortfolioForm({ ...portfolioForm, construction_started_at: e.target.value })} /></Field>
              <Field label="تکمیل"><input className="dev-input" type="date" value={portfolioForm.completed_at} onChange={(e) => setPortfolioForm({ ...portfolioForm, completed_at: e.target.value })} /></Field>
              <Field label="تعداد واحد"><input className="dev-input" type="number" value={portfolioForm.total_units} onChange={(e) => setPortfolioForm({ ...portfolioForm, total_units: e.target.value })} /></Field>
              <Field label="توضیحات"><textarea className="dev-textarea" value={portfolioForm.description} onChange={(e) => setPortfolioForm({ ...portfolioForm, description: e.target.value })} /></Field>
            </div>
            <div className="dev-wizard-nav">
              <button type="button" className="dev-btn secondary" onClick={back}>مرحله قبل</button>
              <button type="submit" className="dev-btn secondary" disabled={!firm || busy}>ثبت پروژه سابق</button>
              <button type="button" className="dev-btn primary" onClick={next} disabled={!firm || busy || portfolioProjects.length === 0}>ادامه به مرجع مشتری</button>
            </div>
          </form>}

          {step === portfolioStep && <div className="dev-panel">
            <h2>موقعیت پروژه سابق</h2>
            <ProjectLocationMap
              latitude={portfolioForm.latitude}
              longitude={portfolioForm.longitude}
              onChange={(point) => setPortfolioForm({ ...portfolioForm, ...point })}
            />
            {portfolioProjects.length > 0 ? (
              <div className="dev-list">
                {portfolioProjects.map((project) => {
                  const location = getLocationFields(project.location);
                  return (
                    <button
                      type="button"
                      className="dev-list-row"
                      key={project.id}
                      onClick={() => setPortfolioForm({
                        name: project.name || '',
                        address: project.address || '',
                        description: project.description || '',
                        stage: project.stage || 'COMPLETED',
                        construction_started_at: project.construction_started_at || '',
                        completed_at: project.completed_at || '',
                        total_units: project.total_units || '',
                        ...location
                      })}
                    >
                      <span>{project.name}</span>
                      <small>{location.latitude && location.longitude ? `${location.latitude}, ${location.longitude}` : 'بدون موقعیت'}</small>
                    </button>
                  );
                })}
              </div>
            ) : <p className="dev-muted">برای رفتن به مرحله مرجع مشتری، حداقل یک پروژه سابق ثبت کنید.</p>}
          </div>}

          {step === referencesStep && <form className="dev-panel wide" onSubmit={addReference}>
            <div className="dev-panel-head">
              <div>
                <h2>مرجع مشتری</h2>
                <p>اطلاعات مشتریان پروژه های قبلی به تیم بررسی کمک می کند کیفیت عملکرد و خدمات سازنده را اعتبارسنجی کند.</p>
              </div>
            </div>
            <div className="dev-form-grid">
              <Field label="پروژه">
                <select className="dev-input" value={referenceForm.project} onChange={(e) => setReferenceForm({ ...referenceForm, project: e.target.value })} required>
                  <option value="">انتخاب پروژه</option>
                  {portfolioProjects.map((project) => <option key={project.id} value={project.id}>{project.name}</option>)}
                </select>
              </Field>
              <Field label="نام مشتری"><input className="dev-input" value={referenceForm.name} onChange={(e) => setReferenceForm({ ...referenceForm, name: e.target.value })} required /></Field>
              <Field label="ایمیل"><input className="dev-input" type="email" value={referenceForm.email} onChange={(e) => setReferenceForm({ ...referenceForm, email: e.target.value })} /></Field>
              <Field label="تلفن"><input className="dev-input" value={referenceForm.phone_number} onChange={(e) => setReferenceForm({ ...referenceForm, phone_number: e.target.value })} /></Field>
              <Field label="آدرس"><input className="dev-input" value={referenceForm.address} onChange={(e) => setReferenceForm({ ...referenceForm, address: e.target.value })} /></Field>
              <Field label="واحد / قرارداد"><input className="dev-input" value={referenceForm.unit_reference} onChange={(e) => setReferenceForm({ ...referenceForm, unit_reference: e.target.value })} /></Field>
            </div>
            <div className="dev-wizard-nav">
              <button type="button" className="dev-btn secondary" onClick={back}>مرحله قبل</button>
              <button type="submit" className="dev-btn secondary" disabled={!referenceForm.project || busy}>ثبت مرجع</button>
              <button type="button" className="dev-btn primary" onClick={next} disabled={busy}>ادامه به بررسی</button>
            </div>
          </form>}

          {step === referencesStep && <div className="dev-panel">
            <h2>پروژه های قابل انتخاب</h2>
            <div className="dev-list">
              {portfolioProjects.map((project) => (
                <div className="dev-list-row static" key={project.id}>
                  <span>{project.name}</span>
                  <small>{project.address || 'بدون آدرس'}</small>
                </div>
              ))}
            </div>
          </div>}

          {step === reviewStep && <div className="dev-panel wide full">
            <div className="dev-panel-head">
              <div><h2>بررسی و پایان ثبت نام</h2><p>اطلاعات سازنده، مدارک و سوابق کاری شما ثبت شده است.</p></div>
              {firm && <span className="dev-badge success">پروفایل ساخته شد</span>}
            </div>
            <div className="dev-summary">
              <div><small>نام سازنده</small><strong>{firm?.display_name || firm?.legal_name || 'ثبت نشده'}</strong></div>
              <div><small>مدارک سابقه</small><strong>{portfolioProjects.length}</strong></div>
              <div><small>پرونده های بررسی</small><strong>{cases.length}</strong></div>
            </div>
            <div className="dev-wizard-nav"><button type="button" className="dev-btn secondary" onClick={back}>مرحله قبل</button><Link className="dev-btn primary" to="/developer-projects">رفتن به ثبت پروژه</Link></div>
          </div>}
        </section>
      </main>
    </div>
  );
}

export default DeveloperRegistration;
